import { PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Input, Select, Table, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useWorkspaceState } from '../WorkspaceContext'
import CreateMemberModal from './CreateMemberModal'
import MemberDrawer from './MemberDrawer'
import { listTeamMembers, teamErrorMessage } from './teamApi'
import type { MemberCounts, MemberStatus, TeamMember, TeamRoleKey } from './teamTypes'
import { TeamAvatar, useTeamFormat } from './teamUi'

const PAGE_SIZE = 20

/**
 * VistoeraTASK-00525 — 成员管理. Server-side search / filter / pagination
 * (indexed query, 20 per page): the full roster is never loaded at once.
 */
export default function TeamMembersTab({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation('team')
  const { dateTime, num } = useTeamFormat()
  const { workspace } = useWorkspaceState()
  const isOwner = workspace?.my_role === 'owner'

  const [items, setItems] = useState<TeamMember[]>([])
  const [counts, setCounts] = useState<MemberCounts | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [role, setRole] = useState<TeamRoleKey | undefined>()
  const [status, setStatus] = useState<MemberStatus | undefined>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const reqSeq = useRef(0)
  const [searchParams, setSearchParams] = useSearchParams()

  // 权限管理 → 创建成员账号 deep-links here (?create=1); open the dialog once.
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setCreateOpen(true)
      const next = new URLSearchParams(searchParams)
      next.delete('create')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [q])

  const load = useCallback(async () => {
    const seq = ++reqSeq.current
    setLoading(true)
    setError(null)
    try {
      const res = await listTeamMembers(workspaceId, {
        q: debouncedQ || undefined,
        role,
        status,
        page,
        page_size: PAGE_SIZE,
      })
      if (seq !== reqSeq.current) return
      setItems(res.data.items)
      setTotal(res.data.total)
      setCounts(res.data.counts)
    } catch (e) {
      if (seq !== reqSeq.current) return
      setError(teamErrorMessage(e, t('members.loadFailed')))
    } finally {
      if (seq === reqSeq.current) setLoading(false)
    }
  }, [workspaceId, debouncedQ, role, status, page, t])

  useEffect(() => {
    void load()
  }, [load])

  const columns: ColumnsType<TeamMember> = [
    {
      title: t('members.col.member'),
      key: 'member',
      render: (_: unknown, r) => (
        <div className="tm-member-cell">
          <TeamAvatar name={r.display_name} size={36} round />
          <div className="tm-member-text">
            <div className="tm-member-name">
              {r.display_name}
              {r.is_self && <span className="tm-self">{t('members.you')}</span>}
            </div>
            <div className="tm-hint">{r.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: t('members.col.role'),
      key: 'role',
      width: 130,
      render: (_: unknown, r) => <span className={`tm-tag tm-tag-${r.role_key}`}>{t(`role.${r.role_key}`)}</span>,
    },
    {
      title: t('members.col.status'),
      key: 'status',
      width: 100,
      render: (_: unknown, r) => (
        <span className={`tm-tag ${r.status === 'active' ? 'tm-tag-on' : 'tm-tag-off'}`}>{t(`status.${r.status}`)}</span>
      ),
    },
    { title: t('members.col.joinedAt'), key: 'joined', width: 170, render: (_: unknown, r) => dateTime(r.joined_at) },
    {
      title: t('members.col.lastLogin'),
      key: 'last',
      width: 170,
      render: (_: unknown, r) => (r.last_login_at ? dateTime(r.last_login_at) : <span className="tm-hint">{t('members.never')}</span>),
    },
    {
      title: t('members.col.credits'),
      key: 'credits',
      width: 110,
      render: (_: unknown, r) =>
        r.month_credits_used === null ? <span className="tm-hint">—</span> : <span>{num(r.month_credits_used)}</span>,
    },
    {
      title: t('members.col.actions'),
      key: 'actions',
      width: 90,
      render: (_: unknown, r) => (
        <Button type="link" size="small" onClick={() => setSelected(r.user_id)} data-testid={`team-member-open-${r.user_id}`}>
          {t('members.manage')}
        </Button>
      ),
    },
  ]

  const cards: Array<{ key: string; label: string; value: number | undefined }> = [
    { key: 'total', label: t('members.cards.total'), value: counts?.total },
    { key: 'admins', label: t('members.cards.admins'), value: counts ? counts.owners + counts.admins : undefined },
    { key: 'members', label: t('members.cards.members'), value: counts?.members },
    { key: 'disabled', label: t('members.cards.disabled'), value: counts?.disabled },
  ]

  return (
    <div className="tm-members" data-testid="team-members">
      <div className="tm-row tm-row-count">
        {cards.map((c) => (
          <div className="tm-card tm-metric" key={c.key}>
            <span className="tm-card-label">{c.label}</span>
            <strong className="tm-metric-value">{c.value ?? '—'}</strong>
          </div>
        ))}
      </div>

      <section className="tm-card">
        <div className="tm-toolbar">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('members.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ maxWidth: 280 }}
            data-testid="team-member-search"
          />
          <Select<TeamRoleKey>
            allowClear
            placeholder={t('members.filterRole')}
            value={role}
            onChange={(v) => {
              setRole(v)
              setPage(1)
            }}
            style={{ width: 140 }}
            options={[
              { value: 'owner', label: t('role.owner') },
              { value: 'admin', label: t('role.admin') },
              { value: 'member', label: t('role.member') },
            ]}
          />
          <Select<MemberStatus>
            allowClear
            placeholder={t('members.filterStatus')}
            value={status}
            onChange={(v) => {
              setStatus(v)
              setPage(1)
            }}
            style={{ width: 140 }}
            options={[
              { value: 'active', label: t('status.active') },
              { value: 'disabled', label: t('status.disabled') },
            ]}
          />
          <div className="tm-toolbar-spacer" />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)} data-testid="team-create-member">
            {t('members.create')}
          </Button>
        </div>

        {error && (
          <Alert
            type="error"
            showIcon
            title={error}
            style={{ marginBottom: 12 }}
            action={
              <Button size="small" onClick={() => void load()}>
                {t('common.retry')}
              </Button>
            }
          />
        )}

        <Table<TeamMember>
          rowKey="user_id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={items}
          locale={{ emptyText: t('members.empty') }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: setPage,
          }}
          onRow={(r) => ({ onClick: () => setSelected(r.user_id), style: { cursor: 'pointer' } })}
          scroll={{ x: 900 }}
        />
      </section>

      <CreateMemberModal
        open={createOpen}
        workspaceId={workspaceId}
        canCreateAdmin={isOwner}
        onClose={() => setCreateOpen(false)}
        onCreated={(m) => {
          setCreateOpen(false)
          void message.success(t('create.done', { name: m.display_name }))
          void load()
        }}
      />
      <MemberDrawer
        workspaceId={workspaceId}
        userId={selected}
        onClose={() => setSelected(null)}
        onChanged={() => void load()}
      />
    </div>
  )
}
